import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { listUsers } from '../../api/users';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const targetFormSchema = z.object({
  userId: z.coerce.number().int().min(1, 'Required'),
  periodMonth: z.coerce.number().int().min(1).max(12),
  periodYear: z.coerce.number().int().min(2000).max(2100),
  leadTarget: z.coerce.number().int().min(0).optional().or(z.literal('')),
  registrationTarget: z.coerce.number().int().min(0).optional().or(z.literal('')),
  admissionTarget: z.coerce.number().int().min(0).optional().or(z.literal('')),
  paymentTarget: z.coerce.number().min(0).optional().or(z.literal('')),
  notes: z.string().trim().max(1000).optional().or(z.literal('')),
});

export type TargetFormValues = z.infer<typeof targetFormSchema>;

interface TargetFormDialogProps {
  open: boolean;
  onClose: () => void;
  errorMessage: string | null;
  defaultPeriodMonth: number;
  defaultPeriodYear: number;
  onSubmit: (values: TargetFormValues) => Promise<void>;
}

export const TargetFormDialog = ({
  open,
  onClose,
  errorMessage,
  defaultPeriodMonth,
  defaultPeriodYear,
  onSubmit,
}: TargetFormDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TargetFormValues>({ resolver: zodResolver(targetFormSchema) });

  const usersQuery = useQuery({
    queryKey: ['users-for-targets'],
    queryFn: () => listUsers({ page: 1, pageSize: 100, isActive: true }),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      reset({
        userId: '' as unknown as number,
        periodMonth: defaultPeriodMonth,
        periodYear: defaultPeriodYear,
        leadTarget: '',
        registrationTarget: '',
        admissionTarget: '',
        paymentTarget: '',
        notes: '',
      });
    }
  }, [open, defaultPeriodMonth, defaultPeriodYear, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set counselor target</DialogTitle>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <TextField
              select
              label="Counselor"
              fullWidth
              error={!!errors.userId}
              helperText={errors.userId?.message}
              defaultValue=""
              {...register('userId')}
            >
              {usersQuery.data?.data.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.role.name})
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField select label="Month" fullWidth defaultValue={defaultPeriodMonth} {...register('periodMonth')}>
                {MONTHS.map((label, index) => (
                  <MenuItem key={label} value={index + 1}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Year" type="number" fullWidth {...register('periodYear')} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Lead target" type="number" fullWidth {...register('leadTarget')} />
              <TextField label="Registration target" type="number" fullWidth {...register('registrationTarget')} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Admission target" type="number" fullWidth {...register('admissionTarget')} />
              <TextField label="Payment target" type="number" fullWidth {...register('paymentTarget')} />
            </Stack>
            <TextField label="Notes" fullWidth multiline minRows={2} {...register('notes')} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save target'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
